/**
 * Single source of truth for Primero Dental Clinic's business facts.
 *
 * Before this module existed, the clinic's phone number was duplicated across
 * nine independent files (components, pages, and both locale JSONs), the
 * address and geo coordinates three times each — one instance missing
 * postalCode — and two different WhatsApp numbers existed with no
 * documentation of which was authoritative. Any bilingual route split would
 * have doubled every one of those duplicates.
 *
 * Rules for this file:
 *
 * 1. Nothing here may be invented. Every value is either observed in the
 *    existing codebase (and therefore inherited, not authored) or absent.
 * 2. Anything the clinic has not confirmed is registered in
 *    CLINIC_VERIFICATION below. That registry is the machine-readable
 *    counterpart to docs/google-business-profile-action-plan.md, and the
 *    placeholder scan in scripts/ reads it.
 * 3. Values still render normally while unverified — a flagged phone number is
 *    better than a blank one, and the current number is presumed working.
 *    The registry records the doubt; it does not suppress the value.
 */

/** How much confidence we have in a business fact. */
export type FactStatus =
  /** Confirmed by the client, or structurally self-evident (e.g. the domain). */
  | "verified"
  /** Currently asserted by the site but never confirmed. Renders, but flagged. */
  | "unverified"
  /** Invented for development. MUST NOT reach production as apparent fact. */
  | "demo";

export interface VerificationItem {
  /** Stable id, referenced from docs and from the placeholder scan. */
  id: string;
  /** What is being claimed, in plain language. */
  claim: string;
  /** The value the site asserts today, verbatim. `null` = nothing asserted. */
  currentValue: string | null;
  status: FactStatus;
  /** Where a visitor can currently see this. */
  whereUsed: string;
  /** The exact question the client/manager must answer. */
  action: string;
}

export const SITE_DOMAIN = "primerodentalclinic.com";

/**
 * Physical location and contact channels.
 *
 * Geo coordinates and the Google Maps place link are inherited from the
 * existing ReviewsSection/QuickActionBar embeds, which resolve to a real
 * "Primero Dental Clinic" place entry — the strongest available evidence
 * short of client confirmation.
 */
export const CLINIC = {
  /** Public-facing brand name. */
  name: "Primero Dental Clinic",
  nameAr: "عيادة بريميرو لطب الأسنان",

  address: {
    street: "50 El Nozha Street",
    streetAr: "٥٠ شارع النزهة",
    district: "Nasr City",
    districtAr: "مدينة نصر",
    city: "Cairo",
    cityAr: "القاهرة",
    country: "EG",
    countryName: "Egypt",
    countryNameAr: "مصر",
    /**
     * Deliberately null, not guessed. One existing schema block omitted
     * postalCode entirely and another never had it; inventing an Egyptian
     * postal code would put a false value into LocalBusiness markup.
     * See CLINIC_VERIFICATION["postal-code"].
     */
    postalCode: null as string | null,
  },

  geo: {
    latitude: 30.0733141,
    longitude: 31.3387106,
    /** ISO 3166-2 subdivision, used by the geo meta tags. */
    region: "EG-C",
  },

  phone: {
    /**
     * Local Egyptian format, as displayed on the site today.
     * Research surfaced a Google Business listing showing a different number;
     * neither is treated as authoritative. See CLINIC_VERIFICATION["phone-primary"].
     */
    displayLocal: "01200093366",
    /** E.164, for tel: links and schema. */
    e164: "+201200093366",
  },

  whatsapp: {
    /** Primary WhatsApp line — the number shown in public-facing copy. */
    primary: "201200093366",
    /**
     * A second number appeared in exactly one file (BookingDialog) and in no
     * public-facing copy. Its purpose is undocumented and it is NOT used until
     * the client confirms it. See CLINIC_VERIFICATION["whatsapp-secondary"].
     */
    secondaryUnconfirmed: "201111113628",
  },

  /** Short Google Maps link, inherited from existing components. */
  mapsShortUrl: "https://maps.app.goo.gl/yxHMrh5jc2PXDpVz9",

  social: {
    twitterHandle: "@PrimeroClinic",
  },
} as const;

/**
 * Opening hours.
 *
 * The existing site rendered a single loose "open daily" string and two
 * different machine-readable hour formats in two schema blocks. Neither was
 * client-confirmed, so this shape is provided for the implementation to
 * consume while the actual values remain a verification item — see
 * CLINIC_VERIFICATION["opening-hours"]. Rendering opening hours that turn out
 * to be wrong is worse than rendering a "call to confirm" prompt, so consumers
 * should check `OPENING_HOURS.status` before presenting these as definitive.
 */
export const OPENING_HOURS = {
  status: "unverified" as FactStatus,
  /**
   * Inherited from the existing copy, which said only that the clinic opens
   * daily. Day-level and holiday/Ramadan variation is unknown.
   */
  days: [
    { dayOfWeek: "Saturday", opens: null, closes: null },
    { dayOfWeek: "Sunday", opens: null, closes: null },
    { dayOfWeek: "Monday", opens: null, closes: null },
    { dayOfWeek: "Tuesday", opens: null, closes: null },
    { dayOfWeek: "Wednesday", opens: null, closes: null },
    { dayOfWeek: "Thursday", opens: null, closes: null },
    { dayOfWeek: "Friday", opens: null, closes: null },
  ],
} as const;

/**
 * Every business claim the site makes, or is expected to make, that the client
 * has not confirmed.
 *
 * This is the authoritative list. docs/google-business-profile-action-plan.md
 * renders it for the manager; the build-time placeholder scan asserts that no
 * `status: "demo"` value reaches production copy unlabelled.
 *
 * Claims marked `unverified` with a non-null currentValue were ALREADY LIVE on
 * the site before this audit. They are recorded here rather than silently
 * deleted, because the clinic may well be entitled to make them — that is a
 * question of evidence, not of code.
 */
export const CLINIC_VERIFICATION: VerificationItem[] = [
  {
    id: "phone-primary",
    claim: "Primary public phone number",
    currentValue: "01200093366",
    status: "unverified",
    whereUsed: "Navbar, ContactSection, QuickActionBar, footer, schema, locale copy",
    action:
      "Confirm the authoritative public number. Research observed a Google Business listing with a different number; do not assume either is correct.",
  },
  {
    id: "whatsapp-secondary",
    claim: "A second WhatsApp number receives booking requests",
    currentValue: "201111113628",
    status: "unverified",
    whereUsed: "Previously BookingDialog only; not currently rendered",
    action:
      "Confirm whether this number is real, monitored, and should receive booking messages. If not, delete it from this file.",
  },
  {
    id: "postal-code",
    claim: "Postal code for LocalBusiness address markup",
    currentValue: null,
    status: "unverified",
    whereUsed: "Omitted from schema and address display",
    action: "Provide the clinic's postal code, or confirm that it should stay omitted.",
  },
  {
    id: "opening-hours",
    claim: "Opening hours, including holiday and Ramadan variations",
    currentValue: "Open daily (no times specified)",
    status: "unverified",
    whereUsed: "Contact section, schema openingHoursSpecification",
    action:
      "Provide per-day opening and closing times plus any Ramadan/holiday schedule. Until then the site says 'call to confirm' rather than stating hours.",
  },
  {
    id: "review-count",
    claim: "Number of Google reviews",
    currentValue: "150+ on the homepage, 28+ on the pediatric page",
    status: "unverified",
    whereUsed: "ReviewsSection, PediatricLandingPage",
    action:
      "Confirm the real figure. The site currently contradicts itself. Note: this figure must never be placed in AggregateRating schema — Google makes self-controlled reviews ineligible for the star feature.",
  },
  {
    id: "average-rating",
    claim: "Average Google rating",
    currentValue: "'5.0 Rating' and '4.9/5 Rating' (contradictory)",
    status: "unverified",
    whereUsed: "Hero badge, promotional page badge",
    action: "Confirm the real average rating and remove the contradiction.",
  },
  {
    id: "years-experience",
    claim: "Years the clinic has been operating",
    currentValue: "20+ Years Experience",
    status: "unverified",
    whereUsed: "WhyChooseUs, hero copy, meta descriptions",
    action: "Confirm the founding year so this can be stated accurately (or derived).",
  },
  {
    id: "patients-treated",
    claim: "Total patients treated",
    currentValue: "Join 10,000+ satisfied patients",
    status: "unverified",
    whereUsed: "Contact/CTA copy",
    action: "Confirm a defensible figure, or approve removing the claim.",
  },
  {
    id: "iso-certification",
    claim: "ISO certification",
    currentValue: "ISO Certified",
    status: "unverified",
    whereUsed: "Promotional landing page badge",
    action:
      "Name the certifying body and the specific standard, and supply the certificate. An unattributed 'ISO Certified' badge is not a usable trust signal.",
  },
  {
    id: "awards",
    claim: "Awards won",
    currentValue: "Award Winning",
    status: "unverified",
    whereUsed: "Promotional landing page badge",
    action: "Name the specific award, the awarding body, and the year.",
  },
  {
    id: "sterilization-standard",
    claim: "100% sterilization",
    currentValue: "100% Sterilization",
    status: "unverified",
    whereUsed: "WhyChooseUs",
    action:
      "Describe the actual sterilization protocol and any standard it follows. '100%' is not a meaningful claim; a named protocol is a much stronger trust signal.",
  },
  {
    id: "lifetime-warranty",
    claim: "Lifetime warranty on implants",
    currentValue: "Lifetime Warranty",
    status: "unverified",
    whereUsed: "Implants page feature list",
    action:
      "Confirm whether a warranty is genuinely offered and supply its written terms. An unqualified 'lifetime warranty' on a medical treatment is a significant liability claim.",
  },
  {
    id: "implant-brands",
    claim: "Implant systems used",
    currentValue: "Premium German & Swiss Systems",
    status: "unverified",
    whereUsed: "Implants page",
    action: "Name the actual implant systems used, or approve removing the origin claim.",
  },
  {
    id: "same-day-implants",
    claim: "Teeth fixed within 24 hours",
    currentValue: "Fixed Teeth in 24 Hours",
    status: "unverified",
    whereUsed: "Implants page feature list",
    action:
      "Confirm whether immediate-loading implants are offered and under which clinical indications, so the claim can be stated with its conditions.",
  },
  {
    id: "success-rate",
    claim: "Treatment success rate",
    currentValue: "'High Success Rate' / 'Success Rate' stat",
    status: "unverified",
    whereUsed: "Root canal and implants pages",
    action:
      "Either supply clinic-specific outcome data, or approve replacing this with general published literature framing that is not presented as a Primero statistic.",
  },
  {
    id: "pain-free",
    claim: "Pain-free / painless treatment",
    currentValue: "Pain-Free Treatment / Painless",
    status: "unverified",
    whereUsed: "Root canal page, pediatric page, stat labels",
    action:
      "Approve softer, defensible phrasing (e.g. comfort-focused, anaesthesia options) — an absolute 'painless' promise about a medical procedure cannot be substantiated.",
  },
  {
    id: "legal-name",
    claim: "Registered legal business name",
    currentValue: null,
    status: "unverified",
    whereUsed: "Not currently asserted; needed for Organization schema",
    action: "Provide the exact registered name as it appears on the licence.",
  },
  {
    id: "og-image",
    claim: "Branded social share image",
    currentValue: "A clinic photo is used as an acknowledged stopgap",
    status: "unverified",
    whereUsed: "DEFAULT_OG_IMAGE in src/lib/seo.ts",
    action: "Supply a branded 1200x630 image.",
  },
  {
    id: "before-after-consent",
    claim: "Patient consent for published before/after imagery",
    currentValue: null,
    status: "unverified",
    whereUsed: "Gallery, before/after sections",
    action:
      "Confirm written patient consent exists for every published clinical photograph. Determines whether the existing gallery may remain online.",
  },
  /*
   * Service-specific unknowns.
   *
   * Surfaced during competitor/content research: the business-level register
   * above covered clinic-wide claims, but each service page has its own
   * evidence dependency. Registering them here keeps one complete list for the
   * manager instead of leaving these buried in per-service `replaceWith` notes.
   */
  {
    id: "laser-equipment",
    claim: "The clinic offers laser dentistry",
    currentValue: "\"Laser Treatment Options\" (locale key painlessLaser)",
    status: "unverified",
    whereUsed: "Pediatric section copy",
    action:
      "Confirm whether a dental laser is genuinely owned and used, and for which treatments. This asserts a capability, not just a benefit — it was left in place rather than deleted because it may well be true, but it must be confirmed or removed.",
  },
  {
    id: "veneer-materials",
    claim: "Veneer materials and dental laboratory used",
    currentValue: null,
    status: "unverified",
    whereUsed: "Veneers and Hollywood-smile service pages (materials section omitted)",
    action:
      "Name the veneer materials actually offered (e.g. porcelain type, composite) and whether lab work is in-house or outsourced. Do not name a commercial brand unless the clinic genuinely uses it.",
  },
  {
    id: "aligner-system",
    claim: "Clear-aligner system offered",
    currentValue: null,
    status: "unverified",
    whereUsed: "Orthodontics service page (brand deliberately unnamed)",
    action:
      "Confirm which aligner system is used, if any. The site deliberately does not name Invisalign or any brand without confirmation.",
  },
  {
    id: "endodontic-specialist",
    claim: "Whether root canal treatment is performed by a specialist endodontist",
    currentValue: null,
    status: "unverified",
    whereUsed: "Root canal service page, doctor profiles",
    action:
      "Confirm whether an endodontic specialist performs these cases or whether they are handled by general dentists. This materially affects what the page may claim.",
  },
  {
    id: "pediatric-sedation",
    claim: "Sedation options available for children",
    currentValue: null,
    status: "unverified",
    whereUsed: "Pediatric service page (framed generically, no options asserted)",
    action:
      "Confirm which behavioural-management and sedation options are genuinely available, and who is qualified to administer them. Do not publish sedation claims without this.",
  },
  {
    id: "pricing-disclosure",
    claim: "Whether price ranges may be published",
    currentValue: null,
    status: "unverified",
    whereUsed: "All service pages currently describe cost FACTORS only, never prices",
    action:
      "Decide whether the clinic wants indicative price ranges published. Research found only one local competitor discloses any pricing, so this is a differentiation opportunity — but no figure may be published without written approval.",
  },
  {
    id: "lead-destination",
    claim: "Where booking requests are durably recorded",
    currentValue: "Nowhere — the form only opens a WhatsApp composer",
    status: "unverified",
    whereUsed: "BookingDialog",
    action:
      "Decide where leads must land (serverless endpoint + email, form service, or CRM webhook). Until then the clinic has no record of booking attempts. This is the highest-value open decision on the project.",
  },
];

/** Convenience lookup used by docs generation and the placeholder scan. */
export function getVerificationItem(id: string): VerificationItem | undefined {
  return CLINIC_VERIFICATION.find((v) => v.id === id);
}
